import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { FaSearch, FaSync, FaExclamationCircle, FaTimesCircle, FaCheckCircle, FaChevronDown, FaChevronUp, FaLock } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";
import Pagination from "../Pagination/Pagination";
import "./AddDonorPaymentStatus.css";

const AddDonorPaymentStatus = () => {
  const [transactions, setTransactions] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [checkingIds, setCheckingIds] = useState({});
  const [selectedFailed, setSelectedFailed] = useState([]);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await axiosInstance.get("/api/payment/status-list");
      if (res.data && res.data.success) {
        const list = res.data.data || [];
        setTransactions(list);

        // AUTO-CHECK: only Pending ones need gateway check
        const pendingItems = list.filter(t => t.txnStatus === 'Pending');
        pendingTxnsCheck(pendingItems);
      }
    } catch (err) {
      console.error("Error loading transaction statuses:", err);
    }
  };

  // Sequentially auto-check all pending txns with 2s gap to avoid hammering gateway
  const pendingTxnsCheck = async (pendingItems) => {
    let anyResolved = false;
    for (const txn of pendingItems) {
      try {
        const res = await axiosInstance.post("/api/payment/check-status", {
          donorId: txn.donorId,
          merchantTxnNo: txn.merchantTxnNo
        });
        if (res.data?.success && res.data.txnStatus === "Success") {
          anyResolved = true;
        }
      } catch (err) {
        // silent — admin will see it on next manual refresh
      }
      // 2 second gap between each gateway call
      await new Promise(r => setTimeout(r, 2000));
    }
    if (anyResolved) fetchTransactions();
  };

  // Verify payment status against ICICI gateway
  const handleManualCheck = async (donorId, merchantTxnNo, id) => {
    setCheckingIds(prev => ({ ...prev, [id]: true }));
    try {
      const res = await axiosInstance.post("/api/payment/check-status", { donorId, merchantTxnNo });
      if (res.data?.success) {
        const status = res.data.txnStatus;
        if (status === "Success") {
          Swal.fire("Payment Successful!", "Gateway confirmed payment. Record updated.", "success");
          fetchTransactions();
        } else if (status === "Failed") {
          Swal.fire("Payment Failed", "Gateway confirmed this transaction failed. Record removed.", "error");
          fetchTransactions();
        } else {
          Swal.fire({ title: 'Still Pending', text: 'Gateway could not confirm yet. Please try again after some time.', icon: 'info', timer: 2500, showConfirmButton: false });
        }
      }
    } catch (err) {
      Swal.fire({ title: 'Gateway Not Responding', text: 'Could not reach ICICI gateway. Please try again after some time.', icon: 'warning', timer: 2500, showConfirmButton: false });
    } finally {
      setCheckingIds(prev => ({ ...prev, [id]: false }));
    }
  };



  const toggleSelectFailed = (donationId) => {
    setSelectedFailed(prev =>
      prev.includes(donationId) ? prev.filter(id => id !== donationId) : [...prev, donationId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedFailed.length === 0) return;
    const confirm = await Swal.fire({
      title: `Delete ${selectedFailed.length} Failed Transaction(s)?`,
      text: 'This will permanently remove selected failed transactions.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;

    for (const donationId of selectedFailed) {
      const txn = transactions.find(t => t.donationId === donationId);
      if (txn) {
        try {
          await axiosInstance.post('/api/payment/mark-failed', { donorId: txn.donorId, merchantTxnNo: txn.merchantTxnNo });
        } catch (err) {}
      }
    }
    setSelectedFailed([]);
    fetchTransactions();
    Swal.fire({ title: 'Deleted', text: 'Selected transactions removed.', icon: 'success', timer: 2000, showConfirmButton: false });
  };

  const filteredTransactions = useMemo(() => {
    let list = [...transactions];
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter(t => 
        (t.name || "").toLowerCase().includes(q) || 
        (t.memberId || "").toLowerCase().includes(q) || 
        (t.mobileNo || "").includes(q) ||
        (t.merchantTxnNo || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      list = list.filter(t => t.txnStatus === statusFilter);
    }
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, searchFilter, statusFilter]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const counts = useMemo(() => ({
    pending: transactions.filter(t => t.txnStatus === "Pending").length,
    failed: transactions.filter(t => t.txnStatus === "Failed").length,
  }), [transactions]);

  return (
    <>
      <Helmet>
        <title>Payment Status Center | Rajput Shiksha Kosh</title>
      </Helmet>
      
      <div className="admin-payment-status-container">
        <h2 className="admin-payment-status-title">
          <FaSync className="admin-payment-status-title-icon" /> Online Payment Status Center
        </h2>

        {/* Dynamic Statistics Cards */}
        <div className="admin-payment-status-stat-grid">
          <div className="admin-payment-status-stat-card card-pending">
            <FaExclamationCircle className="stat-card-icon" />
            <div className="stat-card-info">
              <h3>Pending Gateway Transactions</h3>
              <p>{counts.pending}</p>
            </div>
          </div>
          <div className="admin-payment-status-stat-card card-failed">
            <FaTimesCircle className="stat-card-icon" />
            <div className="stat-card-info">
              <h3>Failed Gateway Transactions</h3>
              <p>{counts.failed}</p>
              <span className="failed-cleanup-note">* Failures auto-delete after 30 days.</span>
            </div>
          </div>
        </div>

        {/* Refresh info note */}
        <div className="admin-payment-status-refresh-note">
          <FaSync style={{ marginRight: 6, color: '#e67e22' }} />
          <span><b>Refresh List</b> re-fetches all Pending &amp; Failed transactions from the database and auto-checks each Pending one against the ICICI gateway in the background (with a 2s gap between each call).</span>
        </div>

        <div className="admin-payment-status-controls">
          <div className="admin-payment-status-search-wrapper">
            <FaSearch className="admin-payment-status-search-icon" />
            <input 
              type="text" 
              className="admin-payment-status-search-input" 
              placeholder="Search Name, Member ID, Mobile, or Txn ID..." 
              value={searchFilter} 
              onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(1); }} 
            />
          </div>
          
          <div className="admin-payment-status-right-controls">
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="admin-payment-status-items-select">
              <option value="">All Statuses</option>
              <option value="Pending">Pending Only</option>
              <option value="Failed">Failed Only</option>
            </select>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-payment-status-items-select">
              <option value="5">5 / page</option>
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
            </select>
            <button className="admin-payment-status-btn-refresh" onClick={fetchTransactions} title="Refresh Table Statuses">
              <FaSync /> Refresh List
            </button>
            {selectedFailed.length > 0 && (
              <button className="admin-payment-status-btn-fail" onClick={handleDeleteSelected}>
                <FaTimesCircle /> Delete Selected ({selectedFailed.length})
              </button>
            )}
          </div>
        </div>

        <div className="admin-payment-status-table-wrapper">
          <table className="admin-payment-status-table">
            <thead>
              <tr>
                <th className="col-sno">S.No</th>
                <th className="col-memberid">Member ID</th>
                <th className="col-date">Initiated Date</th>
                <th className="col-name text-left">Donor Details</th>
                <th className="col-type">Donation Type</th>
                <th className="col-amount">Amount</th>
                <th className="col-status">Status</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.length === 0 ? (
                <tr><td colSpan="8" className="empty-table-fallback">No payment records requiring verification found.</td></tr>
              ) : paginatedTransactions.map((txn, index) => {
                const serialNo = (currentPage - 1) * itemsPerPage + index + 1;
                const isChecking = checkingIds[txn.donationId];

                return (
                  <React.Fragment key={txn.donationId}>
                    <tr className="admin-payment-status-main-row">
                      <td className="col-sno">{serialNo}</td>
                      <td className="col-memberid" style={{ fontWeight: "700" }}>{txn.memberId || "N/A"}</td>
                      <td className="col-date">
                        <div style={{ fontWeight: "600" }}>{new Date(txn.date).toLocaleDateString("en-IN")}</div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {new Date(txn.date).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="col-name text-left">
                        <div className="admin-payment-status-name-wrapper" onClick={() => setExpandedRow(expandedRow === txn.donationId ? null : txn.donationId)}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                             <span style={{ fontWeight: 600 }}>{txn.name}</span>
                             <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{txn.mobileNo}</span>
                          </div>
                          <button className="admin-payment-status-expand-btn">
                            {expandedRow === txn.donationId ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>
                      <td className="col-type"><span className="admin-payment-status-badge-info">{txn.donationType}</span></td>
                      <td className="col-amount" style={{ fontWeight: 700, color: '#e67e22' }}>₹{txn.amount}</td>
                      <td className="col-status">
                        <span className={`status-badge-indicator ${txn.txnStatus.toLowerCase()}`}>
                          {txn.txnStatus}
                        </span>
                      </td>
                      <td className="col-action">
                        {txn.txnStatus === "Pending" ? (
                          <button 
                            className="admin-payment-status-btn-check" 
                            onClick={() => handleManualCheck(txn.donorId, txn.merchantTxnNo, txn.donationId)}
                            disabled={isChecking}
                          >
                            <FaSync className={isChecking ? "spin-icon" : ""} /> {isChecking ? "Querying..." : "Verify"}
                          </button>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={selectedFailed.includes(txn.donationId)}
                              onChange={() => toggleSelectFailed(txn.donationId)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span className="admin-payment-status-locked"><FaLock /> Locked</span>
                          </div>
                        )}
                      </td>
                    </tr>

                    {expandedRow === txn.donationId && (
                      <tr className="admin-payment-status-expanded-row">
                        <td colSpan="8">
                          <div className="admin-payment-status-expanded-content">
                            <div className="admin-payment-status-exp-item">
                              <span className="admin-payment-status-exp-label">Merchant Txn No</span>
                              <span className="admin-payment-status-exp-value select-all-text">{txn.merchantTxnNo}</span>
                            </div>
                            <div className="admin-payment-status-exp-item">
                              <span className="admin-payment-status-exp-label">Father's Name</span>
                              <span className="admin-payment-status-exp-value">{txn.fatherName || "—"}</span>
                            </div>
                            <div className="admin-payment-status-exp-item">
                              <span className="admin-payment-status-exp-label">Gateway Mode</span>
                              <span className="admin-payment-status-exp-value">ICICI Payment Gateway (SALE)</span>
                            </div>
                            {/* Mobile-only items (date, status, action hidden in table cols) */}
                            <div className="admin-payment-status-exp-item mobile-status-action">
                              <span className="admin-payment-status-exp-label">Initiated Date &amp; Time</span>
                              <span className="admin-payment-status-exp-value">
                                {new Date(txn.date).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}
                                {" — "}
                                <span style={{ color: '#64748b', fontWeight: 500 }}>
                                  {new Date(txn.date).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </span>
                            </div>
                            <div className="admin-payment-status-exp-item mobile-status-action">
                              <span className="admin-payment-status-exp-label">Payment Status</span>
                              <span className={`status-badge-indicator ${txn.txnStatus.toLowerCase()}`}>{txn.txnStatus}</span>
                            </div>
                            <div className="admin-payment-status-exp-item mobile-status-action">
                              <span className="admin-payment-status-exp-label">Action</span>
                              {txn.txnStatus === "Pending" ? (
                                <button
                                  className="admin-payment-status-btn-check"
                                  onClick={() => handleManualCheck(txn.donorId, txn.merchantTxnNo, txn.donationId)}
                                  disabled={checkingIds[txn.donationId]}
                                >
                                  <FaSync className={checkingIds[txn.donationId] ? "spin-icon" : ""} />
                                  {checkingIds[txn.donationId] ? "Querying..." : "Verify"}
                                </button>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedFailed.includes(txn.donationId)}
                                    onChange={() => toggleSelectFailed(txn.donationId)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                  />
                                  <span className="admin-payment-status-locked"><FaLock /> Locked</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination totalItems={filteredTransactions.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>
    </>
  );
};

export default AddDonorPaymentStatus;